import { SearchField, GroupedUsers, UserSelectorDialog } from '@halo/ui-chat-react';
import { StateEnum, useGroupMembers } from '@halo/chat-sdk-react';

interface CallInviteDialogProps {
  checkUserKey: boolean;
  sessionId: string;
  callMembersId: string[];
  invitingMembers: string[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onOK: (selected: any[]) => Promise<void>;
  onClose?: () => void;
}

const CallInviteDialog = ({
  checkUserKey,
  sessionId,
  callMembersId,
  invitingMembers,
  isOpen,
  setIsOpen,
  onOK,
}: CallInviteDialogProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const { members, run } = useGroupMembers(sessionId);

  const unJoinMembers = useMemo(() => {
    return members?.flatMap((member) => {
      if (
        !callMembersId?.find((callMemberId) => callMemberId.includes(member.user_id)) &&
        member.state !== StateEnum.DELETE_ACCOUNT
      ) {
        return [{ ...member, disabled: invitingMembers.includes(member.user_id) }];
      }
      return [];
    });
  }, [members, callMembersId, invitingMembers]);

  const listFormatted = useMemo(() => {
    const filteredMembers = unJoinMembers?.filter((member) =>
      member?.name?.toLowerCase().includes(searchValue?.toLowerCase())
    );
    return [{ label: '', items: filteredMembers }];
  }, [unJoinMembers, searchValue]);

  const {
    listFlat,
    selected,
    canSelectList,
    isSelectedAll,
    toggleSelectUser,
    onSelectAllChange,
    onRemove,
    resetDialogState,
  } = UserSelectorDialog.useUserSelector(listFormatted, checkUserKey);

  useLayoutEffect(() => {
    if (!isOpen) {
      resetDialogState();
      setSearchValue('');
    }
  }, [isOpen, resetDialogState]);

  useEffect(() => {
    if (isOpen) {
      return run();
    }
  }, [isOpen, run]);

  return (
    <UserSelectorDialog.Root isOpen={isOpen} setIsOpen={setIsOpen}>
      <UserSelectorDialog.Header title={t('calls.inviteMembers')} />
      <UserSelectorDialog.Body
        selectedList={
          <UserSelectorDialog.SelectedList
            selected={selected}
            onRemove={onRemove}
            checkUserKey={checkUserKey}
          />
        }
      >
        <SearchField searchValue={searchValue} onChange={setSearchValue} />
        <GroupedUsers
          list={listFormatted}
          toggleSelectUser={toggleSelectUser}
          selected={selected}
          checkUserKey={checkUserKey}
        />
        {listFlat.length > 0 && canSelectList.length > 0 && (
          <UserSelectorDialog.SelectAll isChecked={isSelectedAll} onChange={onSelectAllChange} />
        )}
      </UserSelectorDialog.Body>
      <UserSelectorDialog.Footer
        selected={selected}
        onOK={async () => {
          await onOK(selected);
          resetDialogState();
        }}
        cancelText={t('buttons.cancel')}
        okText={t('buttons.invite')}
      />
    </UserSelectorDialog.Root>
  );
};

const MemoizedCallInviteDialog = memo(CallInviteDialog);

export { MemoizedCallInviteDialog as CallInviteDialog };
